import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FilterListIcon from '@mui/icons-material/FilterList';
import api from '../axios';
import Header from '../components/Header';
import ContextNavBar from '../components/ContextNavBar';
import ModuleHeader from '../components/ModuleHeader';
import Footer from '../components/Footer';

const Formandos = () => {
  const [formandos, setFormandos] = useState([]);
  const [mesesDisponiveis, setMesesDisponiveis] = useState([]);
  const [filtroMes, setFiltroMes] = useState('');
  const [filtroAno, setFiltroAno] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogExcluir, setDialogExcluir] = useState(false);
  const [formandoSelecionado, setFormandoSelecionado] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchMesesDisponiveis();
    fetchFormandos();
  }, []);

  useEffect(() => {
    fetchFormandos();
  }, [filtroMes, filtroAno]);

  const fetchMesesDisponiveis = async () => {
    try {
      const response = await api.get('/formandos/meses-disponiveis');
      setMesesDisponiveis(response.data);
    } catch (error) {
      console.error('Erro ao buscar meses disponíveis:', error);
    }
  };

  const fetchFormandos = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filtroMes) params.mes = filtroMes;
      if (filtroAno) params.ano = filtroAno;

      const response = await api.get('/formandos/', { params });
      setFormandos(response.data.formandos || []);
    } catch (error) {
      console.error('Erro ao buscar formandos:', error);
      setError('Erro ao carregar formandos');
    } finally {
      setLoading(false);
    }
  };

  const handleExcluirClick = (formando) => {
    setFormandoSelecionado(formando);
    setDialogExcluir(true);
  };

  const handleConfirmarExclusao = async () => {
    if (!formandoSelecionado) return;

    try {
      await api.delete(`/formandos/${formandoSelecionado.id}`);
      setSuccess(`${formandoSelecionado.aluno_nome} removido dos formandos`);
      setDialogExcluir(false);
      setFormandoSelecionado(null);
      fetchFormandos();
      fetchMesesDisponiveis();
    } catch (error) {
      console.error('Erro ao remover formando:', error);
      setError(error.response?.data?.detail || 'Erro ao remover formando');
      setDialogExcluir(false);
    }
  };

  const limparFiltros = () => {
    setFiltroMes('');
    setFiltroAno('');
  };

  const formatarData = (dataISO) => {
    if (!dataISO) return '-';
    const data = new Date(dataISO);
    return data.toLocaleDateString('pt-BR');
  };

  const anosUnicos = [...new Set(mesesDisponiveis.map(m => m.ano))].sort((a, b) => b - a);

  return (
    <>
      <Header />
      <ContextNavBar context="startfy" />
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} sx={{ flexWrap: 'wrap', gap: { xs: 1, md: 2 } }}>
          <Box display="flex" alignItems="center" gap={1}>
            <EmojiEventsIcon sx={{ fontSize: { xs: 28, md: 40 }, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.3rem', md: '2rem' } }}>
              Formandos
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Filtros */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <FilterListIcon color="action" />
            <Typography variant="subtitle1">Filtros:</Typography>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Mês</InputLabel>
              <Select
                value={filtroMes}
                label="Mês"
                onChange={(e) => setFiltroMes(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {[...new Set(mesesDisponiveis.map(m => m.mes))].map((mes) => (
                  <MenuItem key={mes} value={mes}>
                    {mes}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Ano</InputLabel>
              <Select
                value={filtroAno}
                label="Ano"
                onChange={(e) => setFiltroAno(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {anosUnicos.map((ano) => (
                  <MenuItem key={ano} value={ano}>
                    {ano}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              size="small"
              onClick={limparFiltros}
              disabled={!filtroMes && !filtroAno}
            >
              Limpar Filtros
            </Button>

            <Box sx={{ ml: 'auto' }}>
              <Typography variant="body2" color="text.secondary">
                Total: <strong>{formandos.length}</strong> formandos
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Resumo por mês */}
        {mesesDisponiveis.length > 0 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Resumo por Mês/Ano:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {mesesDisponiveis.map((item) => (
                <Chip
                  key={`${item.mes}-${item.ano}`}
                  label={`${item.mes}/${item.ano} (${item.total_formandos})`}
                  color={
                    filtroMes === item.mes && filtroAno === item.ano
                      ? 'primary'
                      : 'default'
                  }
                  onClick={() => {
                    setFiltroMes(item.mes);
                    setFiltroAno(item.ano);
                  }}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Paper>
        )}

        {/* Tabela de Formandos */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : formandos.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <EmojiEventsIcon sx={{ fontSize: { xs: 40, md: 60 }, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Nenhum formando encontrado
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {filtroMes || filtroAno
                ? 'Tente ajustar os filtros'
                : 'Os alunos que completarem as 8 lições aparecerão aqui'}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    Aluno
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    Email
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    Telefone
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    Mês/Ano
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    Data Conclusão
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    Turma
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    Período
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }} align="center">
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formandos.map((formando) => (
                  <TableRow key={formando.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <EmojiEventsIcon color="warning" fontSize="small" />
                        <strong>{formando.aluno_nome}</strong>
                      </Box>
                    </TableCell>
                    <TableCell>{formando.aluno_email || '-'}</TableCell>
                    <TableCell>{formando.aluno_telefone || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={`${formando.mes}/${formando.ano}`}
                        color="success"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatarData(formando.data_conclusao)}</TableCell>
                    <TableCell>{formando.turma_nome || '-'}</TableCell>
                    <TableCell>{formando.periodo || '-'}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleExcluirClick(formando)}
                        title="Remover dos formandos"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Dialog Confirmar Exclusão */}
        <Dialog open={dialogExcluir} onClose={() => setDialogExcluir(false)}>
          <DialogTitle>Remover Formando</DialogTitle>
          <DialogContent>
            <Typography>
              Tem certeza que deseja remover <strong>{formandoSelecionado?.aluno_nome}</strong> da
              lista de formandos?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              O aluno poderá se matricular em novas turmas novamente.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogExcluir(false)}>Cancelar</Button>
            <Button onClick={handleConfirmarExclusao} color="error" variant="contained">
              Confirmar Remoção
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      <Footer />
    </>
  );
};

export default Formandos;
